// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { PurchaseOrder } from '@/purchase-order/api/types';
import {
  PurchaseOrderForm,
  PurchaseOrderFormData,
} from '@/purchase-order/components/purchase-order-form';
import { usePurchaseOrderFileUploads } from '@/purchase-order/hooks/use-purchase-order-file-uploads';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { useCallback, useState } from 'react';

interface Props {
  /**
   * Whether the dialog is open.
   */
  isOpen: boolean;

  /**
   * Callback fired when the dialog is closed.
   */
  onClose: () => void;

  /**
   * Optional purchase order to edit. If provided, the dialog will be in edit mode.
   */
  purchaseOrder?: PurchaseOrder;

  /**
   * Optional initial form data to pre-populate the form.
   * Used for cloning a purchase order with pre-filled values.
   */
  initialFormData?: PurchaseOrderFormData;
}

const FORM_ID = 'purchase-order-form';

/**
 * Dialog for creating or editing a purchase order.
 */
export function PurchaseOrderDialog(props: Props) {
  const [isPending, setIsPending] = useState(false);
  const { user } = useCurrentUser();
  const isEditMode = !!props.purchaseOrder;
  const fileUploads = usePurchaseOrderFileUploads();

  const handlePendingChange = useCallback((pending: boolean) => {
    setIsPending(pending);
  }, []);

  // File upload state affects submit button
  const canSubmit =
    !isPending && !fileUploads.isAnyUploading && !fileUploads.hasFailedUploads;

  const handleAfterClose = useCallback(() => {
    fileUploads.reset();
  }, [fileUploads]);

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={props.isOpen}
      onClose={props.onClose}
      scroll="paper"
      TransitionProps={{
        onExited: handleAfterClose,
      }}
    >
      <DialogTitle>
        {isEditMode ? 'Edit purchase order' : 'Add purchase order'}
      </DialogTitle>
      <DialogContent dividers>
        <PurchaseOrderForm
          key={props.isOpen ? 'open' : 'closed'}
          purchaseOrder={props.purchaseOrder}
          initialFormData={props.initialFormData}
          orgDefaultReceiveLocation={user.organization.defaultReceiveLocation}
          onSubmit={props.onClose}
          onPendingChange={handlePendingChange}
          formId={FORM_ID}
          fileUploadProps={
            isEditMode
              ? undefined
              : {
                  files: fileUploads.files,
                  onFilesSelected: fileUploads.addFiles,
                  onRemoveFile: fileUploads.removeFile,
                  disabled: isPending,
                }
          }
          getFileIds={isEditMode ? undefined : fileUploads.getCompletedFileIds}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>Cancel</Button>
        <Button
          type="submit"
          form={FORM_ID}
          variant="contained"
          color="primary"
          disabled={!canSubmit}
          startIcon={
            isPending || fileUploads.isAnyUploading ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {isEditMode ? 'Save changes' : 'Create purchase order'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
